import { addDays } from 'date-fns'
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react'
import React from 'react'

import CopyButton from 'components/reusable/CopyButton'
import { Badge, Banner, Button, TextLink } from 'slab'
import { capitalize, conditionallyLocalizeDate, supportLinks } from 'utils'
import { type MembersCoveredParticipant, MembersCoveredParticipantQualifyingLifeEventStatus, type MembersEnrollment, MembersEnrollmentOverviewProgressState, MembersEnrollmentProgressEventState } from 'utils/web_api.schemas'

import { enrollmentDisplayHelper } from '../../utils'
import { CarrierLogo } from '../CarrierLogo'
import QLEBanner from './QLEBanner'
import CoveredDependent from '../CoveredDependent/CoveredDependent'
import CoveredDependentDetails from '../CoveredDependentDetails/CoveredDependentDetails'
import EnrollmentProgress from '../EnrollmentProgress/EnrollmentProgress'
import FinancialStatusIndicator from '../FinancialStatusIndicator/FinancialStatusIndicator'
import TrackerSegment from '../TrackerSegment/TrackerSegment'
import { WhoPaysBadge } from '../WhoPaysBadge/WhoPaysBadge'

type EnrollmentDetailsProps = {
  enrollment: MembersEnrollment,
  isPriorYear: boolean,
  setShowHeader?: (show: boolean) => void,
}

const horizontalDivider = <div className='border-b border-gray-200 my-2' />

const renderCoveredParticipant = (enrollment: MembersEnrollment, participant: MembersCoveredParticipant) => {
  const today = new Date()
  let participantDetails: React.ReactNode | undefined

  const possessiveParticipant = participant.given_name.endsWith('s') ? `${participant.given_name}'` : `${participant.given_name}'s`
  const makeParticipantMessage = (startOrEndVerb: string, theDate: Date) => {
    return <>{possessiveParticipant} coverage {startOrEndVerb} on <span className='font-bold'>{conditionallyLocalizeDate(theDate)}</span> due to a qualifying life event</>
  }

  if (enrollment.coverage_dates?.coverage_start_date && participant?.coverage_start_date) {
    const enrollmentStartDate = new Date(enrollment.coverage_dates.coverage_start_date)
    const participantStartDate = new Date(participant.coverage_start_date)
    if (participantStartDate > enrollmentStartDate) {
      const alreadyStarted = participantStartDate <= today
      participantDetails = makeParticipantMessage(alreadyStarted ? 'started' : 'will start', participantStartDate)
    }
  }

  // It's more important to show the coverage will end, hence if instead of if-else
  if (enrollment.coverage_dates?.coverage_end_date && participant?.coverage_end_date) {
    const enrollmentEndDate = new Date(enrollment.coverage_dates.coverage_end_date)
    const participantEndDate = new Date(participant.coverage_end_date)
    if (participantEndDate < enrollmentEndDate) {
      const alreadyEnded = participantEndDate < today
      participantDetails = makeParticipantMessage(alreadyEnded ? 'ended' : 'will end', participantEndDate)
    }
  }

  if (participant.show_backdated_coverage_message && participant.coverage_start_date) {
    participantDetails = makeParticipantMessage('started', new Date(participant.coverage_start_date))
  }

  const trackerProgressState = participant.qualifying_life_event_status === MembersCoveredParticipantQualifyingLifeEventStatus.awaiting_confirmation
    ? MembersEnrollmentOverviewProgressState.needs_attention
    : MembersEnrollmentOverviewProgressState.on_track

  return (
    <CoveredDependent
      key={participant.id}
      coveredParticipant={participant}
      participantEnrollmentProgressState={participant.qualifying_life_event_status}
    >
      {participant.qualifying_life_event_status && (
        <div className='flex mt-4' data-testid='participant-tracker-segment'>
          <TrackerSegment state={MembersEnrollmentProgressEventState.completed} label='Received' progressState={trackerProgressState} />
          <TrackerSegment
            state={
              participant.qualifying_life_event_status === MembersCoveredParticipantQualifyingLifeEventStatus.complete
                ? MembersEnrollmentProgressEventState.completed
                : MembersEnrollmentProgressEventState.processing
            }
            label='Submitted'
            progressState={trackerProgressState}
          />
          <TrackerSegment
            state={
              participant.qualifying_life_event_status === MembersCoveredParticipantQualifyingLifeEventStatus.complete
                ? MembersEnrollmentProgressEventState.completed
                : MembersEnrollmentProgressEventState.pending
            }
            label='Enrolled'
            progressState={trackerProgressState}
          />
        </div>
      )}
      {participantDetails && <span className='body-sm text-gray-500 mt-4'>{participantDetails}</span>}
    </CoveredDependent>
  )
}

const gradientBackground: React.CSSProperties = {
  background: 'linear-gradient(90deg, #E5E5E5 -0.74%, #002677 100.74%)',
}

const EnrollmentDetails: React.FC<EnrollmentDetailsProps> = ({
  enrollment,
  isPriorYear,
  setShowHeader,
}) => {
  const { startDate, endDate, showTracker, showFinancialStatus, showWhoPays } = enrollmentDisplayHelper(enrollment, isPriorYear)
  const [viewDetails, setViewDetails] = React.useState(false)

  const smallHeading = enrollment.plan_name || enrollment.carrier_name
  const { selfParticipant, dependents } = React.useMemo(() => ({
    selfParticipant: enrollment.covered_participants.find(c => c.relationship === 'self'),
    dependents: enrollment.covered_participants.filter(c => c.relationship !== 'self')
  }), [enrollment])

  const isEnrollmentStarted = React.useMemo(() => {
    const startDateStr = enrollment.coverage_dates?.coverage_start_date
    if (startDateStr) return Date.now() >= new Date(startDateStr).getTime()
    return false
  }, [enrollment])

  // We will hide the banner after 2 months, since some carriers won't return the participant member ids
  // and based off the data we have, most of the member ids we get are available within 60 days of enrollment submission
  const isEnrollmentStartedOver2Months = React.useMemo(() => {
    const startDateStr = enrollment.coverage_dates?.coverage_start_date
    if (startDateStr) return Date.now() >= (addDays(new Date(startDateStr), 60).getTime())
    return false
  }, [enrollment])

  if (viewDetails) {
    return (
      <div>
        <div className='-ml-4'>
          <Button
            leftIcon={ArrowLeftIcon}
            size='small'
            variant='tertiary-deemphasized'
            onClick={() => {
              setViewDetails(false)
              setShowHeader?.(true)
            }}
          >
            Back to {capitalize(enrollment.coverage_type)}
          </Button>
        </div>
        <div className='heading-lg text-main mt-4 mb-6'>Member details</div>
        {
              enrollment.covered_participants.length > 0 && (
                <div className='flex flex-col'>
                  {
                    enrollment.covered_participants.map((participant) => (
                      <CoveredDependentDetails key={participant.id} coveredParticipant={participant} />
                    ))
                  }
                </div>
              )
            }
      </div>
    )
  }

  return (
    <div className='w-full flex flex-col gap-4'>
      {horizontalDivider}

      <div className='flex flex-row justify-between items-start gap-4'>
        <div className='flex flex-col gap-1'>
          <span className='heading-sm text-main'>{smallHeading}</span>
          {enrollment.plan_name && <span className='body text-gray-500'>{enrollment.carrier_name}</span>}
        </div>
        {enrollment.carrier_image_url && <CarrierLogo src={enrollment.carrier_image_url} />}
      </div>

      {dependents.map(dependent => <QLEBanner dependent={dependent} key={dependent.id} />)}

      {showTracker && (
        <div className='flex flex-col gap-2 rounded-xl border border-gray-100 p-4'>
          <div className='body text-gray-500'>Status</div>
          <EnrollmentProgress
            state={enrollment.overview!.progress_state as MembersEnrollmentOverviewProgressState}
            showCaption
            startDate={startDate!}
          />
          <div className='flex gap-1 mt-4' data-testid='tracker-segment'>
            {enrollment.overview?.progress_events?.map((event) => (
              <TrackerSegment
                key={event.label}
                state={event.state}
                label={event.label}
                completionDate={event.completion_date ? new Date(event.completion_date) : undefined}
                progressState={enrollment.overview!.progress_state!}
              />
            ))}
          </div>
        </div>
      )}

      {horizontalDivider}

      <div className='flex flex-row justify-between'>
        <div className='heading-sm'>Plan details</div>
        {enrollment.coverage_details_url && (
          <div className='flex flex-row justify-start my-2'>
            <TextLink
              href={enrollment.coverage_details_url}
              target='_blank'
              rightIcon={ArrowUpRightIcon}
              color='purple'
            >
              View coverage details
            </TextLink>
          </div>
        )}
      </div>

      {showFinancialStatus && (
        <div className='flex flex-col gap-2'>
          <div className='body text-gray-500'>Status</div>
          <FinancialStatusIndicator
            status={enrollment.overview!.financial_status!}
            carrierName={enrollment.carrier_name}
            coverageEndDate={endDate}
            showCaption
          />
        </div>
      )}

      <div className='flex flex-col gap-2'>
        <div className='body text-gray-500'>Monthly premium</div>
        <div className='flex flex-row items-center justify-start gap-4'>
          <div className='body text-main'>{enrollment.monthly_premium ? enrollment.monthly_premium.formatted_amount : '-'}</div>
          {showWhoPays && <WhoPaysBadge thatchPays={enrollment.thatch_pays!} />}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <div className='body text-gray-500'>Coverage period</div>
        <div className='body text-main'>{startDate ? conditionallyLocalizeDate(startDate) : '(Unknown)'} - {endDate ? conditionallyLocalizeDate(endDate) : '(Unknown)'}</div>
      </div>

      {
        selfParticipant && (
          <>
            {horizontalDivider}
            <div className='flex justify-between'>
              <div className='heading-sm'>Members</div>
              <TextLink
                color='purple'
                onClick={() => {
                  setViewDetails(true)
                  setShowHeader?.(false)
                }}
              >
                <div className='cursor-pointer'>View all member details</div>
              </TextLink>
            </div>
            <div className='p-4 rounded-xl border border-gray-100 shadow-md'>
              <div className='flex justify-between'>
                <div className='flex-col gap-1'>
                  <div className='body'>{selfParticipant.given_name} {selfParticipant.family_name}</div>
                  <div className='body-sm text-gray-500'>You</div>
                </div>
                <div>
                  <Badge type='info'>Primary member</Badge>
                </div>
              </div>
              {
                enrollment.off_thatch_enrollment
                  ? null
                  : (
                    <>
                      <div className='my-4 border-dashed border-t' />
                      {
                      enrollment.carrier_member_id
                        ? (
                          <div>
                            <div className='body-sm text-gray-500'>Member ID</div>
                            <div className='heading-sm flex gap-1 items-center'>
                              <div className='font-mono'>{enrollment.carrier_member_id}</div>
                              <CopyButton valueToCopy={enrollment.carrier_member_id} variant='default' />
                            </div>
                          </div>
                          )
                        : isEnrollmentStartedOver2Months
                          ? (
                            <TextLink
                              data-testid='member-id-link'
                              href={supportLinks.memberId}
                              target='_blank'
                            >
                              Where can I find my member ID?
                            </TextLink>
                            )
                          : (
                            <div className='flex flex-col items-center'>
                              <div className='mb-2 body-sm text-gray-500'>Syncing member ID</div>
                              {enrollment.carrier_image_url && (
                                <div className='flex gap-1 items-center'>
                                  <div className='p-2 rounded-lg border border-gray-200 p-1'>
                                    <img
                                      height='20'
                                      width='54'
                                      src={RAILS_ASSET_URL('/logo_all_cap.svg')}
                                    />
                                  </div>
                                  <div className='h-[1px] w-[77px]' style={gradientBackground} />
                                  <CarrierLogo src={enrollment.carrier_image_url} />
                                </div>
                              )}
                            </div>
                            )
                    }
                    </>
                    )
              }
            </div>

            {dependents?.length
              ? (
                <div className='flex flex-col gap-2'>
                  <div className='body-bold mt-6'>Covered dependents</div>

                  {dependents?.map((participant) => (
                    renderCoveredParticipant(enrollment, participant)
                  ))}
                </div>
                )
              : null}

            {
              !enrollment.carrier_member_id && isEnrollmentStarted && !isEnrollmentStartedOver2Months && !enrollment.off_thatch_enrollment && (
                <Banner
                  type='info'
                  title="We don't have your member ID yet"
                >
                  <p>
                    Since your coverage has started, you can
                    {' '}
                    <TextLink
                      data-testid='member-id-link'
                      href={supportLinks.memberId}
                      target='_blank'
                    >
                      check with
                    </TextLink>
                    {' '}
                    {enrollment.carrier_name}
                    {' '}
                    while we work on syncing it to Thatch
                  </p>
                </Banner>
              )
            }
          </>
        )
      }
    </div>
  )
}

export default EnrollmentDetails
